import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Inbox, Loader2, Send, UserCheck, XCircle } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  useMyRequests, useRequestInbox, useClaimRequest, useAssignRequest, usePatchRequest,
} from "@/hooks/useInternalRequests";
import { useAuthStore } from "@/store/useAuthStore";
import { CreateInternalRequestDrawer } from "@/components/requests/CreateInternalRequestDrawer";
import { RequestDetailDrawer } from "@/components/requests/RequestDetailDrawer";
import { CompleteRequestModal } from "@/components/requests/CompleteRequestModal";
import { REQUEST_STATUS_LABEL, REQUEST_TYPE_LABEL, type InboxDepartment, type RequestRow, type RequestStatus } from "@/api/internalRequests";

/**
 * 3B «Запити у відділи» — manager «Мої запити» + Department inbox (responsibles/
 * head). Request goes to a DEPARTMENT queue (assignedUser=NULL until claim/assign).
 */

const STATUS_CLS: Record<RequestStatus, string> = {
  new: "bg-info text-info-foreground",
  assigned: "bg-info text-info-foreground",
  in_progress: "bg-warning text-warning-foreground",
  // DS-CANON-ALLOW: needs_info attention orange (D-096 DR-1=C) — ЄДИНИЙ дозволений raw-orange
  // (attention-стан, не error). Задокументований виняток AC-DS-CANON; не множити.
  needs_info: "bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300",
  done: "bg-success text-success-foreground",
  rejected: "bg-destructive-soft text-destructive-soft-foreground", // DS-2.1: AA 5.47:1 (D-096 DR-2)
  cancelled: "bg-muted text-muted-foreground",
};

function StatusBadge({ s, overdue }: { s: RequestStatus; overdue?: boolean }) {
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", STATUS_CLS[s])}>
      {REQUEST_STATUS_LABEL[s]}{overdue && <> · прострочено <AlertTriangle className="h-3 w-3" /></>}
    </span>
  );
}

type CompleteTarget = { id: string; subject: string; requestNumber?: string | null } | null;

/** #9 S3 — VISUAL inbox buckets (canonical module; flat list = legacy). */
const BUCKETS: { key: string; title: string; match: (r: RequestRow, myId: string) => boolean }[] = [
  { key: "new", title: "Нові", match: (r) => r.status === "new" },
  { key: "mine", title: "Призначені мені", match: (r, myId) => r.status === "assigned" && r.assignedUserId === myId },
  { key: "assigned_other", title: "Призначені (інші)", match: (r, myId) => r.status === "assigned" && r.assignedUserId !== myId },
  { key: "in_progress", title: "В роботі", match: (r) => r.status === "in_progress" },
  { key: "needs_info", title: "Очікує уточнення", match: (r) => r.status === "needs_info" },
  { key: "closed", title: "Закриті (30 днів)", match: (r) => r.status === "done" || r.status === "rejected" },
];

export function RequestsListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [completeTarget, setCompleteTarget] = useState<CompleteTarget>(null);
  const [completeMode, setCompleteMode] = useState<"complete" | "reject">("complete");
  // D-043 S2 — deep-link ?open=:id (notification CTA → request drawer; ідіома
  // TasksListPage). Параметр знімається при закритті drawer-а.
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get("open");
  // Supervisor Home C2 deep-link (?filter=sla) — focus the dept inbox on SLA-
  // breached requests. The inbox is already department-gated (no scope=team needed;
  // backend never broadens — out-of-dept requests are simply not returned).
  const slaOnly = searchParams.get("filter") === "sla";
  const clearSla = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("filter");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  useEffect(() => {
    if (openParam) setDetailId(openParam);
  }, [openParam]);
  const closeDetail = useCallback(() => {
    setDetailId(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("open");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  const myId = useAuthStore((s) => s.user?.id ?? "");
  const isAdmin = useAuthStore((s) => s.user?.type === "admin");
  const myQ = useMyRequests(true);
  const inboxQ = useRequestInbox(true);

  // canonical module ⇒ rows carry requestNumber (#9 flag ON).
  const canonical = useMemo(
    () => !!inboxQ.data?.departments.some((d) => d.items.some((i) => i.requestNumber !== undefined)),
    [inboxQ.data],
  );
  const openComplete = (r: RequestRow, mode: "complete" | "reject") => {
    setCompleteTarget({ id: r.id, subject: r.subject, requestNumber: r.requestNumber });
    setCompleteMode(mode);
  };

  return (
    <div className="mx-auto max-w-5xl p-6 md:p-8">
      <div className="mb-5 flex items-center gap-3">
        <h1 className="mr-auto text-xl font-bold">Запити у відділи</h1>
        {/* D-044 S3 — IR Admin drawer АБСОРБОВАНО у Admin Center #6 (AC-ADM-050). */}
        {isAdmin && (
          <Link to="/admin/routes" className="rounded-md border px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground" title="Відділи / маршрути / SLA — Admin Center #6">
            Налаштування
          </Link>
        )}
        <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90">
          <Send className="h-4 w-4" /> Запит у відділ
        </button>
      </div>

      {/* Мої запити */}
      <section className="mb-8">
        <h2 className="mb-2 flex items-center gap-2 text-base font-semibold">
          Мої запити <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">погляд менеджера</span>
        </h2>
        {myQ.isPending ? <Skel /> : (myQ.data?.items.length ?? 0) === 0 ? (
          <Empty
            text="Запитів ще немає."
            cta={
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              >
                <Send className="h-4 w-4" /> Запит у відділ
              </button>
            }
          />
        ) : (
          <div className="overflow-hidden rounded-xl border">
            <table className="w-full border-collapse text-sm">
              <thead><tr className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                <Th>Тема</Th><Th>Відділ</Th><Th>Хто взяв</Th><Th>Статус</Th><Th>Дедлайн</Th><Th>Результат</Th>
              </tr></thead>
              <tbody>
                {myQ.data!.items.map((r) => (
                  <tr key={r.id} className="cursor-pointer border-t hover:bg-muted/20" onClick={() => setDetailId(r.id)}>
                    <td className="px-3 py-2.5">
                      <div className="font-medium">
                        {r.requestNumber && <span className="mr-1.5 font-mono text-[11px] text-muted-foreground">{r.requestNumber}</span>}
                        {r.subject}
                      </div>
                      <div className="text-[11px] text-muted-foreground">{REQUEST_TYPE_LABEL[r.requestType as keyof typeof REQUEST_TYPE_LABEL] ?? r.requestType}{r.holdingName ? ` · ${r.holdingName}` : ""}</div>
                    </td>
                    <td className="px-3 py-2.5"><Dept name={r.departmentName} /></td>
                    <td className="px-3 py-2.5">{r.assigneeName ?? <span className="text-muted-foreground">у черзі відділу</span>}</td>
                    <td className="px-3 py-2.5"><StatusBadge s={r.status} overdue={r.overdue} /></td>
                    <td className={cn("px-3 py-2.5", r.overdue && "font-medium text-warning-foreground")}>{r.dueDate ?? "—"}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{r.result ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Department inbox */}
      <section>
        <h2 className="mb-2 flex items-center gap-2 text-base font-semibold">
          Вхідні відділу <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">погляд відповідального / голови</span>
          {slaOnly && (
            <button onClick={clearSla} className="ml-auto rounded-full border border-warning-foreground/40 bg-warning px-2.5 py-0.5 text-[11px] font-medium text-warning-foreground hover:bg-warning-foreground/10">
              Лише прострочені SLA · показати всі ✕
            </button>
          )}
        </h2>
        {inboxQ.isPending ? <Skel /> : (inboxQ.data?.departments.length ?? 0) === 0 ? (
          <Empty text="Ви не є відповідальним жодного відділу." />
        ) : (
          <div className="space-y-4">
            {inboxQ.data!.departments.map((d) => (
              <DeptQueue key={d.id} d={d} canonical={canonical} myId={myId} slaOnly={slaOnly}
                onOpen={setDetailId} onComplete={openComplete} />
            ))}
          </div>
        )}
      </section>

      <CreateInternalRequestDrawer open={createOpen} onClose={() => setCreateOpen(false)} />
      <RequestDetailDrawer requestId={detailId} onClose={closeDetail} />
      <CompleteRequestModal target={completeTarget} mode={completeMode} onClose={() => setCompleteTarget(null)} />
    </div>
  );
}

/** Dept queue: #9 canonical → VISUAL 6 бакетів (+overdue overlay); legacy → flat. */
function DeptQueue({ d, canonical, myId, slaOnly, onOpen, onComplete }: {
  d: InboxDepartment; canonical: boolean; myId: string; slaOnly?: boolean;
  onOpen: (id: string) => void; onComplete: (r: RequestRow, mode: "complete" | "reject") => void;
}) {
  const overdueCount = d.items.filter((r) => (r.overdue || r.slaOverdue) && !["done", "rejected", "cancelled"].includes(r.status)).length;
  // C2 ?filter=sla — narrow to SLA-breached open rows (header counts stay full).
  const items = slaOnly
    ? d.items.filter((r) => (r.overdue || r.slaOverdue) && !["done", "rejected", "cancelled"].includes(r.status))
    : d.items;
  return (
    <div className="overflow-hidden rounded-xl border">
      <div className="flex items-center gap-2 border-b bg-muted/40 px-4 py-2.5 text-sm font-semibold">
        <Inbox className="h-4 w-4 text-primary" /> {d.name} — черга
        <span className="ml-auto flex items-center gap-2 text-[11px] font-normal text-muted-foreground">
          {overdueCount > 0 && <span className="font-medium text-warning-foreground">{overdueCount} прострочено ⚠</span>}
          {d.items.filter((i) => i.status === "new").length} нові
        </span>
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">
          {slaOnly ? "Немає прострочених SLA." : "Черга порожня."}
        </div>
      ) : canonical ? (
        BUCKETS.map((b) => {
          const rows = items.filter((r) => b.match(r, myId));
          if (rows.length === 0) return null;
          return (
            <div key={b.key}>
              <div className="border-b bg-muted/20 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {b.title} · {rows.length}
              </div>
              {rows.map((r) => <InboxRow key={r.id} r={r} isHead={d.isHead} responsibles={d.responsibles} onOpen={onOpen} onComplete={onComplete} />)}
            </div>
          );
        })
      ) : (
        items.map((r) => <InboxRow key={r.id} r={r} isHead={d.isHead} responsibles={d.responsibles} onOpen={onOpen} onComplete={onComplete} />)
      )}
    </div>
  );
}

function InboxRow({ r, isHead, responsibles, onOpen, onComplete }: {
  r: RequestRow; isHead: boolean; responsibles: { id: string; name: string }[];
  onOpen: (id: string) => void; onComplete: (r: RequestRow, mode: "complete" | "reject") => void;
}) {
  const claim = useClaimRequest();
  const assign = useAssignRequest();
  const patch = usePatchRequest();
  const [assignOpen, setAssignOpen] = useState(false);
  const busy = claim.isPending || assign.isPending || patch.isPending;
  const workable = r.status === "assigned" || r.status === "in_progress";

  return (
    <div className="cursor-pointer border-b px-4 py-3 last:border-b-0 hover:bg-muted/10" onClick={() => onOpen(r.id)}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">
            {r.requestNumber && <span className="mr-1.5 font-mono text-[11px] text-muted-foreground">{r.requestNumber}</span>}
            {r.subject}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            від {r.requesterName ?? "—"}{r.holdingName ? ` · ${r.holdingName}` : ""} · дедлайн {r.dueDate ?? "—"}
            {(r.overdue || r.slaOverdue) && <span className="font-medium text-warning-foreground">· прострочено ⚠</span>}
            <StatusBadge s={r.status} />
            {r.assigneeName && <span>· виконавець: {r.assigneeName}</span>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {r.status === "new" && (
            <button disabled={busy} onClick={() => claim.mutate(r.id)} className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {claim.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />} Взяти
            </button>
          )}
          {r.canAssign && isHead && responsibles.length > 0 && (
            <button disabled={busy} onClick={() => setAssignOpen((v) => !v)} className="rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted" title="Тільки керівник відділу">Призначити ▾</button>
          )}
          {r.status === "assigned" && (
            <button disabled={busy} onClick={() => patch.mutate({ id: r.id, status: "in_progress" })} className="rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted">Почати</button>
          )}
          {workable && (
            <>
              <button disabled={busy} onClick={() => onComplete(r, "complete")} className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary-dark">
                <CheckCircle2 className="h-3.5 w-3.5" /> Виконано
              </button>
              <button disabled={busy} onClick={() => onComplete(r, "reject")} className="inline-flex items-center gap-1 rounded-md border border-destructive/40 px-2.5 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/5">
                <XCircle className="h-3.5 w-3.5" /> Відхилити
              </button>
            </>
          )}
        </div>
      </div>

      {assignOpen && (
        <div className="mt-2 flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2" onClick={(e) => e.stopPropagation()}>
          <span className="text-xs text-muted-foreground">Призначити:</span>
          <select onChange={(e) => { if (e.target.value) { assign.mutate({ id: r.id, userId: e.target.value }); setAssignOpen(false); } }} className="rounded border border-input bg-background px-2 py-1 text-xs" defaultValue="">
            <option value="">— виконавець —</option>
            {responsibles.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) { return <th className="px-3 py-2 text-left font-semibold">{children}</th>; }
function Dept({ name }: { name: string | null }) {
  return <span className="rounded bg-violet text-violet-foreground px-1.5 py-0.5 text-[11px] font-semibold">{name ?? "—"}</span>;
}
function Skel() { return <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />)}</div>; }
function Empty({ text, cta }: { text: string; cta?: ReactNode }) { return <div className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">{text}{cta ? <div className="mt-3">{cta}</div> : null}</div>; }
